import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchNewspapers } from "../services/newspaperService";
import HeroSection from "../components/HeroSection";
import Seo from "../components/Seo";
import {
  SITE,
  absoluteUrl,
  toIstIso,
  toMetaDescription,
  toTagList,
} from "../utils/seo";

function ReadMore() {
  // the route is /read/:date but the value is the newspaper id (e.g. /read/20)
  // or its slug, depending on where the link was generated
  const { date: paperKey } = useParams();
  const navigate = useNavigate();
  const [papers, setPapers] = useState([]);
  const pdfRef = useRef(null);

  useEffect(() => {
    fetchNewspapers().then(setPapers);
  }, []);

  const currentPaper = papers.find(
    (p) => String(p.id) === String(paperKey) || p.slug === paperKey
  );

  const printPDF = () => {
    if (pdfRef.current) {
      pdfRef.current.contentWindow.print();
    }
  };

  if (!currentPaper) return <p>Loading...</p>;

  /* ===== SEO values for this newspaper ===== */
  const canonicalPath = `/read/${currentPaper.slug || currentPaper.id}`;
  const canonicalUrl = absoluteUrl(canonicalPath);
  const publishedTime =
    toIstIso(currentPaper.published_time) ||
    toIstIso(currentPaper.created_at) ||
    toIstIso(currentPaper.date);
  const modifiedTime =
    toIstIso(currentPaper.modified_time) ||
    toIstIso(currentPaper.updated_at) ||
    publishedTime;
  const section = currentPaper.section || SITE.defaultSection;
  const tags = toTagList(currentPaper.tags);
  const description = toMetaDescription(
    currentPaper.teaser_text || currentPaper.content,
    `${currentPaper.title} - ${currentPaper.date} का ई-पेपर Etawah Times पर पढ़ें।`
  );
  const imageUrl = absoluteUrl(currentPaper.image_url || SITE.logo);

  const newsArticleSchema = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    mainEntityOfPage: { "@type": "WebPage", "@id": canonicalUrl },
    headline: String(currentPaper.title).slice(0, 110),
    description,
    image: [imageUrl],
    datePublished: publishedTime,
    dateModified: modifiedTime,
    inLanguage: SITE.language,
    articleSection: section,
    keywords: tags.join(", "),
    isAccessibleForFree: true,
    author: { "@type": "Organization", name: SITE.name, url: SITE.url },
    publisher: {
      "@type": "NewsMediaOrganization",
      name: SITE.name,
      url: SITE.url,
      logo: { "@type": "ImageObject", url: absoluteUrl(SITE.logo) },
    },
    ...(currentPaper.pdf_url && {
      associatedMedia: {
        "@type": "MediaObject",
        contentUrl: currentPaper.pdf_url,
        encodingFormat: "application/pdf",
      },
    }),
  };

  return (
    <div className="py-3">
      <Seo
        type="article"
        title={currentPaper.title}
        description={description}
        canonical={canonicalPath}
        image={imageUrl}
        publishedTime={publishedTime}
        modifiedTime={modifiedTime}
        section={section}
        tags={tags}
        jsonLd={newsArticleSchema}
      />

      <HeroSection pageType="other" />
      <h1 className="mt-3 h2">{currentPaper.title}</h1>

      {/* Date Dropdown - navigating keeps the URL, canonical and meta tags in sync */}
      <select
        value={currentPaper.slug || currentPaper.id}
        onChange={(e) => navigate(`/read/${e.target.value}`)}
        className="form-select w-25"
      >
        {papers.map(p => (
          <option key={p.id} value={p.slug || p.id}>
            {p.date}
          </option>
        ))}
      </select>

      <br /><br />

      {/* Buttons */}
      {/* <button onClick={printPDF}>Print Newspaper</button> */}

      <a href={currentPaper.pdf_url} target="_blank" rel="noreferrer">
        <button style={{ marginLeft: "10px" }} className="btn btn-primary">Open PDF</button>
      </a>

      <br /><br />

      {/* PDF Viewer */}
      <iframe
        ref={pdfRef}
        src={currentPaper.pdf_url}
        title={`${currentPaper.title} - Etawah Times e-paper PDF`}
        width="100%"
        height="600px"
        style={{ border: "1px solid #ccc" }}
      />
    </div>
  );
}

export default ReadMore;
